import { Input, Typography, theme } from "antd";
import type { TextAreaProps } from "antd/es/input/TextArea";
import {
	type CSSProperties,
	useCallback,
	useLayoutEffect,
	useRef,
	useState,
} from "react";
import { tokenizePlaceholders } from "@/common/placeholders";

// Box and text metrics copied from the real textarea onto the highlight layer, so the
// two lay text out identically whatever the theme, size or browser defaults are.
const MIRRORED_STYLES = [
	"paddingTop",
	"paddingRight",
	"paddingBottom",
	"paddingLeft",
	"borderTopWidth",
	"borderRightWidth",
	"borderBottomWidth",
	"borderLeftWidth",
	"fontFamily",
	"fontSize",
	"fontWeight",
	"fontStyle",
	"fontStretch",
	"fontVariant",
	"fontFeatureSettings",
	"fontVariationSettings",
	"fontKerning",
	"textRendering",
	"textTransform",
	"lineHeight",
	"letterSpacing",
	"wordSpacing",
	"tabSize",
] as const;

interface PlaceholderTextAreaProps
	extends Omit<TextAreaProps, "showCount" | "autoSize" | "rows"> {
	/** Minimum visible rows; the field grows with its content. */
	rows?: number;
	/** Shows a `length / maxLength` counter under the field (needs `maxLength`). */
	showCount?: boolean;
}

/**
 * A textarea that highlights every `{{key}}` placeholder token as it is typed. The
 * text stays in a plain transparent textarea (so editing, selection and undo are
 * native); a mirrored layer behind it paints a chip under each token.
 */
export function PlaceholderTextArea({
	rows = 4,
	showCount,
	value,
	maxLength,
	style,
	...props
}: PlaceholderTextAreaProps) {
	const { token } = theme.useToken();
	const wrapperRef = useRef<HTMLDivElement>(null);
	const backdropRef = useRef<HTMLDivElement>(null);
	const [box, setBox] = useState<CSSProperties>({});

	// The textarea is found through the DOM rather than antd's ref, whose internals
	// vary by version.
	const measure = useCallback(() => {
		const el = wrapperRef.current?.querySelector("textarea");
		const wrapper = wrapperRef.current;
		if (!el || !wrapper) return;
		const computed = getComputedStyle(el);
		const elRect = el.getBoundingClientRect();
		const wrapperRect = wrapper.getBoundingClientRect();
		const next: CSSProperties = {
			top: elRect.top - wrapperRect.top,
			left: elRect.left - wrapperRect.left,
			width: el.offsetWidth,
			height: el.offsetHeight,
		};
		for (const prop of MIRRORED_STYLES) {
			(next as Record<string, string>)[prop] = computed[prop];
		}
		setBox(next);
	}, []);

	useLayoutEffect(() => {
		const el = wrapperRef.current?.querySelector("textarea");
		if (!el) return;
		measure();
		const observer = new ResizeObserver(measure);
		observer.observe(el);
		return () => observer.disconnect();
	}, [measure]);

	const text = typeof value === "string" ? value : "";

	return (
		<>
			<div
				ref={wrapperRef}
				style={{
					position: "relative",
					background: token.colorBgContainer,
					borderRadius: token.borderRadius,
				}}
			>
				<div
					ref={backdropRef}
					aria-hidden
					style={{
						...box,
						position: "absolute",
						boxSizing: "border-box",
						borderStyle: "solid",
						borderColor: "transparent",
						overflow: "hidden",
						whiteSpace: "pre-wrap",
						overflowWrap: "break-word",
						color: "transparent",
						pointerEvents: "none",
					}}
				>
					{tokenizePlaceholders(text).map((part, i) =>
						part.key ? (
							<mark
								// biome-ignore lint/suspicious/noArrayIndexKey: parts are positional and re-derived from the text on every render
								key={i}
								style={{
									color: "transparent",
									background: token.colorPrimaryBg,
									boxShadow: `0 0 0 1px ${token.colorPrimaryBorder}`,
									borderRadius: 3,
									padding: 0,
								}}
							>
								{part.text}
							</mark>
						) : (
							part.text
						),
					)}
					{/* A trailing newline needs a character after it to take up a line. */}
					{text.endsWith("\n") ? " " : null}
				</div>
				<Input.TextArea
					value={value}
					maxLength={maxLength}
					autoSize={{ minRows: rows }}
					onScroll={(e) => {
						if (backdropRef.current) {
							backdropRef.current.scrollTop = e.currentTarget.scrollTop;
						}
					}}
					style={{
						...style,
						position: "relative",
						background: "transparent",
					}}
					{...props}
				/>
			</div>
			{showCount && maxLength !== undefined && (
				<Typography.Text
					type="secondary"
					style={{ display: "block", textAlign: "right", fontSize: 12 }}
				>
					{text.length} / {maxLength}
				</Typography.Text>
			)}
		</>
	);
}
